/* Camera acquisition, and the honest reporting of its failures.
 *
 * A generic "camera unavailable" is useless in a warehouse. The three states the
 * owner called out need three different actions from the technician:
 *
 *   permission-denied  -> the OS/browser has to be changed, and on iOS Safari
 *                         that means Settings, not a retry button. Retrying is
 *                         actively misleading here.
 *   no-camera          -> nothing to fix; type the serial.
 *   camera-in-use      -> close the other app (or the other tab) and retry.
 *                         A retry button IS the right affordance.
 *
 * Plus two more the platform actually produces and that would otherwise be
 * misreported as one of the above: an insecure origin (no getUserMedia at all
 * outside https/localhost) and constraints nothing satisfies.
 *
 * `getUserMedia` is injected so the classification is testable without a device.
 */

export type CameraFailure =
  | { kind: 'permission-denied'; message: string }
  | { kind: 'no-camera'; message: string }
  | { kind: 'camera-in-use'; message: string }
  | { kind: 'insecure-context'; message: string }
  | { kind: 'unsupported'; message: string }
  | { kind: 'overconstrained'; constraint: string; message: string }
  | { kind: 'aborted'; message: string }
  | { kind: 'unknown'; name: string; message: string };

/**
 * Map a getUserMedia rejection to a state we can act on.
 *
 * The name checks come in pairs because the spec renamed several of these and
 * shipped browsers still throw both spellings: NotReadableError/TrackStartError,
 * NotFoundError/DevicesNotFoundError, NotAllowedError/PermissionDeniedError.
 */
export function classifyCameraError(err: unknown): CameraFailure {
  const name =
    typeof err === 'object' && err !== null && 'name' in err ? String((err as Error).name) : '';
  const raw =
    typeof err === 'object' && err !== null && 'message' in err
      ? String((err as Error).message)
      : String(err);

  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return {
        kind: 'permission-denied',
        message:
          'Camera access is blocked for this site. On iPhone: Settings → Safari → Camera, or the "aA" menu in the address bar. You can type the serial instead.',
      };
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return {
        kind: 'no-camera',
        message: 'This device has no camera available. Type the serial instead.',
      };
    case 'NotReadableError':
    case 'TrackStartError':
      return {
        kind: 'camera-in-use',
        message:
          'The camera is being used by another app or tab. Close it and try again, or type the serial.',
      };
    case 'OverconstrainedError': {
      const constraint =
        typeof err === 'object' && err !== null && 'constraint' in err
          ? String((err as { constraint?: unknown }).constraint ?? '')
          : '';
      return {
        kind: 'overconstrained',
        constraint,
        message: `No camera on this device matches what the scanner asked for${
          constraint ? ` (${constraint})` : ''
        }. Type the serial instead.`,
      };
    }
    case 'AbortError':
      return { kind: 'aborted', message: 'Starting the camera was interrupted. Try again.' };
    default:
      return { kind: 'unknown', name: name || 'Error', message: raw };
  }
}

export interface CameraPorts {
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  /** Whether the page can use getUserMedia at all (https or localhost). */
  isSecureContext(): boolean;
  /** Whether the API exists on this browser. */
  hasMediaDevices(): boolean;
}

export type CameraOpen =
  | { ok: true; stream: MediaStream; torch: TorchControl | null }
  | { ok: false; failure: CameraFailure };

/**
 * Torch, only when the runtime says it is really controllable.
 *
 * `torch` support on iOS Safari could not be resolved from documentation, and
 * WebKit's implementation may or may not be behind a feature flag on a shipped
 * build — so this feature-detects at runtime rather than branching on platform.
 * A torch button that does nothing is worse than no torch button in a basement.
 */
export interface TorchControl {
  on(): Promise<void>;
  off(): Promise<void>;
}

function torchFor(stream: MediaStream): TorchControl | null {
  const track = stream.getVideoTracks()[0];
  if (!track || typeof track.getCapabilities !== 'function') return null;
  const caps = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
  if (caps?.torch !== true) return null;
  const set = (torch: boolean) =>
    track.applyConstraints({ advanced: [{ torch } as MediaTrackConstraintSet] });
  return { on: () => set(true), off: () => set(false) };
}

/** Constraints for scanning: rear camera, as much resolution as we can get. */
export const SCAN_CONSTRAINTS: MediaStreamConstraints = {
  audio: false,
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  },
};

/**
 * Open the camera.
 *
 * Never throws; every path produces either a stream or a named failure, because
 * the caller has to render a specific instruction and "something went wrong" is
 * not an instruction.
 */
export async function openCamera(
  ports: CameraPorts,
  constraints: MediaStreamConstraints = SCAN_CONSTRAINTS,
): Promise<CameraOpen> {
  if (!ports.isSecureContext()) {
    return {
      ok: false,
      failure: {
        kind: 'insecure-context',
        message:
          'The camera only works over a secure (https) connection. Type the serial instead.',
      },
    };
  }
  if (!ports.hasMediaDevices()) {
    return {
      ok: false,
      failure: {
        kind: 'unsupported',
        message: 'This browser cannot open the camera. Type the serial instead.',
      },
    };
  }
  try {
    const stream = await ports.getUserMedia(constraints);
    return { ok: true, stream, torch: torchFor(stream) };
  } catch (err) {
    return { ok: false, failure: classifyCameraError(err) };
  }
}

/** True when retrying could plausibly work. Drives whether a retry button shows. */
export function isRetryable(failure: CameraFailure): boolean {
  return (
    failure.kind === 'camera-in-use' ||
    failure.kind === 'aborted' ||
    failure.kind === 'unknown'
  );
}

/** Default ports, wired to the real browser. */
export function browserCameraPorts(): CameraPorts {
  return {
    getUserMedia: (c) => navigator.mediaDevices.getUserMedia(c),
    isSecureContext: () => typeof window !== 'undefined' && window.isSecureContext === true,
    hasMediaDevices: () =>
      typeof navigator !== 'undefined' &&
      typeof navigator.mediaDevices?.getUserMedia === 'function',
  };
}

export function stopStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((t) => t.stop());
}
